import * as Ramda from 'ramda';
import {
  PrimeXProductTypes,
  ProductTypes,
  type AccountDto,
  type ProductType,
} from '../../types';
import type { FirmographicDataDto } from '../../types/firmo/firmographic-data.dto';
import {
  COUNTRY_MAPPING,
  languages,
  type Countries,
  type Languages,
} from '../i18n.language-config';
import { getCowbellTranslations } from '../translations';

/**
 * Panels that lay a label out to the left of its value align the address right so it
 * meets the value column; stacked label-above-value panels pass 'left'.
 */
interface AddressAlignmentParams {
  textAlign?: 'left' | 'right';
}

interface FormatUKAddressParams {
  address1: string | undefined;
  address2: string | undefined;
  address3: string | undefined;
  city: string | undefined;
  zipCode: string | undefined;
}

export function FormatUKAddress({
  address1 = '',
  address2 = '',
  address3 = '',
  city = '',
  zipCode = '',
  textAlign = 'right',
  ...props
}: FormatUKAddressParams & AddressAlignmentParams) {
  const addressLines = [address1, address2, address3].filter(Boolean);

  const cityAndPostcodeParts = [city, zipCode].filter(Boolean);

  return (
    <div style={{ textAlign }} {...props}>
      {addressLines.length > 0 &&
        addressLines.map((line, index) => (
          <div key={`address-${line}-${index}`}>{line}</div>
        ))}
      <div>
        {cityAndPostcodeParts.length &&
          cityAndPostcodeParts.join(', ').toUpperCase()}
      </div>
    </div>
  );
}

interface FormatUSAddressParams {
  address1: string | undefined;
  address2: string | undefined;
  city: string | undefined;
  state: string | undefined;
  zipCode: string | undefined;
}

function FormatUSAddress({
  address1 = '',
  address2 = '',
  city = '',
  state = '',
  zipCode = '',
  textAlign = 'right',
  ...props
}: FormatUSAddressParams & AddressAlignmentParams) {
  const addressLines = [address1, address2].filter(Boolean);

  return (
    <div style={{ textAlign }} {...props}>
      {addressLines.length > 0 &&
        addressLines.map((line, index) => (
          <div key={`address-${line}-${index}`}>{line}</div>
        ))}
      <div>
        {city}, {state} {zipCode}
      </div>
    </div>
  );
}

export function formatAddressByRegion(
  region: Languages,
  addressOptions: FormatUKAddressParams &
    FormatUSAddressParams &
    AddressAlignmentParams
) {
  switch (region) {
    case languages['en-GB']: {
      return FormatUKAddress(addressOptions);
    }

    default:
      return FormatUSAddress(addressOptions);
  }
}

export const UkAddressFromApi = {
  address1: 'Suite 123',
  address2: 'King Charles III House',
  address3: '1001 Cowbell Avenue',
  city: 'GLASGOW',
  zipCode: 'EH7 4RT',
};

export const getRegionByProduct = (productType: ProductType): Languages => {
  if (
    productType === ProductTypes.prime_one ||
    productType === ProductTypes.p_one_plus ||
    productType === ProductTypes.prime_one_tech ||
    productType === ProductTypes.prime_one_tech_plus
  ) {
    return languages['en-GB'];
  }

  if (productType === ProductTypes.prime_one_au) {
    return languages['en-AU'];
  }

  return languages['en-US'];
};

const POUND_SYMBOL = '£';
const DOLLAR_SYMBOL = '$';

export const getCurrencySymbolByProduct = (productType: ProductType) => {
  if (
    productType === ProductTypes.prime_one ||
    productType === ProductTypes.p_one_plus ||
    productType === ProductTypes.prime_one_tech ||
    productType === ProductTypes.prime_one_tech_plus
  ) {
    return POUND_SYMBOL;
  }

  return DOLLAR_SYMBOL;
};

export const getCurrencySymbolByLanguage = (language: Languages) => {
  if (language === languages['en-GB']) {
    return POUND_SYMBOL;
  }

  return DOLLAR_SYMBOL;
};

export const getLanguageFromAccount = (
  account: AccountDto | FirmographicDataDto
) => {
  if (!account.country) {
    return languages['en-US'];
  }

  return COUNTRY_MAPPING[account.country as Countries];
};

/**
 * Accepts a nullish product: callers on optional-product screens legitimately have none, and a
 * missing product simply is not a PrimeX one, so it falls through to "Deductible".
 */
export const getDeductibleLanguage = (product?: ProductType | null) => {
  const isPrimeXProduct = Object.values(PrimeXProductTypes).some(
    Ramda.equals(product)
  );
  const { t } = getCowbellTranslations();
  if (!isPrimeXProduct) {
    return 'Deductible';
  }

  return t('retention');
};

export const getSupportEmailByRegion = (region: Languages) => {
  switch (region) {
    case languages['en-GB']: {
      return 'UKsupport@cowbellcyber.ai';
    }
    case languages['en-AU']: {
      return 'support@cowbell.au';
    }

    default:
      return 'support@cowbellcyber.ai';
  }
};

export const getClaimsSupportContactInfoByLanguage = (language: Languages) => {
  switch (language) {
    case languages['en-GB']:
      return {
        email: 'ukclaims@cowbellcyber.ai',
        phone: '0800 208 8106',
      };
    case languages['en-AU']: {
      return {
        email: 'claims@cowbell.au',
        phone: '+61 1 800 844 989',
      };
    }
    case languages['en-US']:
      return {
        email: 'claims@cowbellcyber.ai',
        phone: '(833) 633-8666',
      };
    default:
      throw new Error(`Unsupported language: ${language}`);
  }
};
